import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import type { Field } from "@project/admin/components/resource-table";
import { bhojanCategoryService } from "@project/services/admin/bhojan-category.service";
import { bhojanService } from "@project/services/admin/bhojan.service";
import {
  BHOJAN_SPONSORSHIP_TYPE_OPTIONS,
  toBhojanPrice,
  type BhojanPackage,
  type BhojanPackagePayload,
  type BhojanSponsorshipType,
} from "@project/types/bhojan";

const fields: Field[] = [
  {
    name: "category",
    label: "Category",
    required: true,
    showInTable: true,
    lookup: { table: "bhojan_categories", labelField: "name" },
  },
  {
    name: "sponsorships_type",
    label: "Sponsorship Type",
    type: "select",
    required: true,
    showInTable: true,
    placeholder: "Select type...",
    options: BHOJAN_SPONSORSHIP_TYPE_OPTIONS,
  },
  {
    name: "name",
    label: "Package Name",
    required: true,
    showInTable: true,
    placeholder: "Lunch and Dinner",
  },
  {
    name: "description",
    label: "Description",
    type: "textarea",
    showInTable: true,
    placeholder: "Sponsor both lunch and evening dinner service.",
  },
  {
    name: "price",
    label: "Price (₹)",
    type: "number",
    required: true,
    showInTable: true,
    currency: true,
    placeholder: "21000",
  },
  {
    name: "is_active",
    label: "Active",
    type: "boolean",
    showInTable: true,
  },
];

function toSponsorshipType(value: unknown): BhojanSponsorshipType {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (raw === "other" || raw === "other meal sponsorship") {
    return "other";
  }
  if (raw === "special" || raw === "special service sponsorships") {
    return "special";
  }
  throw new Error("Select a valid sponsorship type.");
}

function toPayload(payload: Record<string, unknown>): BhojanPackagePayload {
  return {
    category: Number(payload.category),
    name: String(payload.name ?? "").trim(),
    description: payload.description
      ? String(payload.description).trim()
      : undefined,
    price: toBhojanPrice(payload.price),
    sponsorships_type: toSponsorshipType(payload.sponsorships_type),
    is_active: payload.is_active === true || payload.is_active === "true",
  };
}

export default function Page() {
  return (
    <AdminShell
      title="Bhojan Packages"
      subtitle="Manage meal packages linked to Bhojanshala categories"
    >
      <ApiResourceTable<BhojanPackage>
        title="Package"
        labelField="name"
        fields={fields}
        searchFields={[
          "name",
          "description",
          "category_name",
          "sponsorships_type",
        ]}
        defaultValues={{
          is_active: true,
          sponsorships_type: "other",
        }}
        statusFilterOptions={[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ]}
        lookupOptions={{
          sponsorships_type: BHOJAN_SPONSORSHIP_TYPE_OPTIONS,
        }}
        csvFileName="bhojan-packages"
        lookupSearchLoaders={{
          category: query => bhojanCategoryService.listLookup(query),
        }}
        list={query => bhojanService.listPage(query)}
        create={payload => bhojanService.create(toPayload(payload))}
        update={(id, payload) => bhojanService.update(id, toPayload(payload))}
        remove={id => bhojanService.delete(id)}
      />
    </AdminShell>
  );
}
